"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _throttle = _interopRequireDefault(require("lodash/throttle"));

var _propTypes2 = _interopRequireDefault(require("@styled-system/prop-types"));

var _useResizeObserver = _interopRequireDefault(require("../../hooks/__internal__/useResizeObserver"));

var _formSummary = _interopRequireDefault(require("./__internal__/form-summary.component"));

var _form = require("./form.style");

var _sidebar = require("../sidebar/sidebar.component");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const SCROLL_THROTTLE = 50;

const Form = ({
  children,
  saveButton,
  leftSideButtons,
  rightSideButtons,
  errorCount,
  warningCount,
  onSubmit,
  buttonAlignment = "right",
  stickyFooter,
  dialogRef,
  fieldSpacing = 3,
  noValidate = true,
  ...rest
}) => {
  const [isFooterSticky, setIsFooterSticky] = (0, _react.useState)(false);
  const {
    isInSidebar
  } = (0, _react.useContext)(_sidebar.SidebarContext);
  const formRef = (0, _react.useRef)();
  const formFooterRef = (0, _react.useRef)();
  const stickyListenersAddedRef = (0, _react.useRef)(false);
  const checkStickyFooter = (0, _react.useCallback)((0, _throttle.default)(() => {
    const {
      bottom
    } = formRef.current.getBoundingClientRect();
    let isBottomBelowScreen;

    if (dialogRef) {
      isBottomBelowScreen = bottom > dialogRef.current.getBoundingClientRect().bottom;
    } else {
      isBottomBelowScreen = bottom > window.innerHeight;
    }

    if (isBottomBelowScreen) {
      setIsFooterSticky(true);
    } else {
      setIsFooterSticky(false);
    }
  }, SCROLL_THROTTLE), []);
  (0, _useResizeObserver.default)(formRef, checkStickyFooter, !stickyFooter);
  const addStickyFooterListeners = (0, _react.useCallback)(() => {
    window.addEventListener("resize", checkStickyFooter, true);
    window.addEventListener("scroll", checkStickyFooter, true);
    stickyListenersAddedRef.current = true;
  }, [checkStickyFooter]);
  const removeStickyFooterListeners = (0, _react.useCallback)(() => {
    window.removeEventListener("resize", checkStickyFooter, true);
    window.removeEventListener("scroll", checkStickyFooter, true);
    stickyListenersAddedRef.current = false;
  }, [checkStickyFooter]);
  (0, _react.useEffect)(() => {
    if (stickyFooter && !stickyListenersAddedRef.current) {
      addStickyFooterListeners();
      checkStickyFooter();
    }

    return () => removeStickyFooterListeners();
  }, [addStickyFooterListeners, checkStickyFooter, removeStickyFooterListeners, stickyFooter]);
  return /*#__PURE__*/_react.default.createElement(_form.StyledForm, _extends({
    ref: formRef,
    stickyFooter: stickyFooter && isFooterSticky,
    onSubmit: onSubmit,
    "data-component": "form",
    fieldSpacing: fieldSpacing,
    noValidate: noValidate,
    isInSidebar: isInSidebar
  }, rest), children, /*#__PURE__*/_react.default.createElement(_form.StyledFormFooter, {
    "data-element": "form-footer",
    className: isFooterSticky ? "sticky" : "",
    ref: formFooterRef,
    stickyFooter: isFooterSticky,
    buttonAlignment: buttonAlignment
  }, leftSideButtons && /*#__PURE__*/_react.default.createElement(_form.StyledLeftButtons, {
    buttonAlignment: buttonAlignment
  }, leftSideButtons), /*#__PURE__*/_react.default.createElement(_formSummary.default, {
    errors: errorCount,
    warnings: warningCount
  }, saveButton), rightSideButtons && /*#__PURE__*/_react.default.createElement(_form.StyledRightButtons, {
    buttonAlignment: buttonAlignment
  }, rightSideButtons)));
};

Form.propTypes = { ..._propTypes2.default.space,

  /** Alignment of buttons */
  buttonAlignment: _propTypes.default.oneOf(["left", "right"]),

  /** Enables the sticky footer. */
  stickyFooter: _propTypes.default.bool,

  /** Additional buttons rendered on the left side of the save button */
  leftSideButtons: _propTypes.default.node,

  /** Additional buttons rendered on the right side of the save button */
  rightSideButtons: _propTypes.default.node,

  /** Callback passed to the form element */
  onSubmit: _propTypes.default.func,

  /** Child elements */
  children: _propTypes.default.node,

  /** Save button to be rendered */
  saveButton: _propTypes.default.node,

  /** The total number of errors present in the form */
  errorCount: _propTypes.default.number,

  /** The total number of warnings present in the form */
  warningCount: _propTypes.default.number,

  /** Spacing between form fields, given number will be multiplied by base spacing unit (8) */
  fieldSpacing: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 7]),

  /** Disable HTML5 validation */
  noValidate: _propTypes.default.bool,

  /**
   * @private
   * @ignore
   * Used to detect if FormFooter should be sticky when used in Dialog component
   */
  dialogRef: _propTypes.default.shape({
    current: _propTypes.default.any
  })
};
var _default = Form;
exports.default = _default;